/**
 * This browser's push subscription (PUSH_NOTIFICATIONS_PLAN.md).
 *
 * The worker (`src/outpost-sw.ts`) shows the notifications; this module only
 * asks the browser's push service for a subscription and hands its endpoint
 * and keys to the backend (`features/settings/pushApi.ts`). Permission is
 * asked only from a tap (ruling 14): {@link subscribe} is the one call that
 * can raise the browser's prompt, and nothing calls it on load.
 *
 * The server never sends the endpoint back, so the id of this browser's row is
 * kept in `localStorage` under `outpost:push:<user_id>`, next to the endpoint
 * it was made for. That is how the Settings card knows which device row is
 * "this device" and how sign-out finds the row to delete.
 */

/** iOS and iPadOS have the push API only inside the installed app. */
export function isPushSupported(): boolean {
  return (
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  );
}

/** How long to wait for the worker to be active before giving up. */
const WORKER_WAIT_MS = 10_000;

/**
 * The worker's registration once it is active, or null (dev has no worker).
 * Polls `getRegistration()` rather than awaiting `serviceWorker.ready`, which
 * never settles when nothing is registered.
 */
async function activeRegistration(): Promise<ServiceWorkerRegistration | null> {
  if (!isPushSupported()) return null;
  const deadline = Date.now() + WORKER_WAIT_MS;
  for (;;) {
    const registration = await navigator.serviceWorker.getRegistration();
    if (!registration) return null;
    if (registration.active) return registration;
    if (Date.now() > deadline) return null;
    await new Promise((resolve) => window.setTimeout(resolve, 250));
  }
}

/** This browser's subscription, or null when it has none (or no worker). */
export async function currentSubscription(): Promise<PushSubscription | null> {
  const registration = await activeRegistration();
  if (!registration) return null;
  return registration.pushManager.getSubscription();
}

/** URL-safe base64 (padding optional) to bytes. */
export function base64UrlToBytes(value: string): Uint8Array<ArrayBuffer> {
  const base64 = value.replace(/-/g, '+').replace(/_/g, '/');
  const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
  const binary = window.atob(padded);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) bytes[index] = binary.charCodeAt(index);
  return bytes;
}

/** Bytes to URL-safe base64 without padding, the form the backend checks. */
function bytesToBase64Url(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return window.btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/**
 * Whether the subscription was made with this server key. A rotated VAPID key
 * leaves every old subscription unusable, so a mismatch means subscribe again.
 * A browser that does not expose the key is given the benefit of the doubt.
 */
export function subscriptionMatchesKey(subscription: PushSubscription, publicKey: string): boolean {
  const used = subscription.options.applicationServerKey;
  if (!used) return true;
  const expected = base64UrlToBytes(publicKey);
  const actual = new Uint8Array(used);
  if (actual.length !== expected.length) return false;
  return actual.every((byte, index) => byte === expected[index]);
}

export class PushUnavailableError extends Error {
  constructor() {
    super('Notifications cannot be set up in this browser right now.');
    this.name = 'PushUnavailableError';
  }
}

/**
 * Subscribes this browser with the server's key. Asks for notification
 * permission first when it has not been granted, so call it only from a tap.
 * Throws a `DOMException` (`NotAllowedError`) when the user says no.
 */
export async function subscribe(publicKey: string): Promise<PushSubscription> {
  const registration = await activeRegistration();
  if (!registration) throw new PushUnavailableError();
  return registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: base64UrlToBytes(publicKey),
  });
}

/** Drops this browser's subscription at the push service. Never throws. */
export async function unsubscribe(): Promise<void> {
  try {
    const subscription = await currentSubscription();
    if (subscription) await subscription.unsubscribe();
  } catch {
    // Nothing to undo: the push service drops it on its own when it is gone.
  }
}

/** The endpoint and keys in the shape `POST /me/push-subscriptions` takes. */
export function subscriptionKeys(
  subscription: PushSubscription,
): { endpoint: string; keys: { p256dh: string; auth: string } } | null {
  const p256dh = subscription.getKey('p256dh');
  const auth = subscription.getKey('auth');
  if (!p256dh || !auth) return null;
  return {
    endpoint: subscription.endpoint,
    keys: { p256dh: bytesToBase64Url(p256dh), auth: bytesToBase64Url(auth) },
  };
}

const LABEL_MAX = 80;

function browserName(ua: string): string {
  if (/Edg(e|A|iOS)?\//.test(ua)) return 'Edge';
  if (/OPR\/|Opera/.test(ua)) return 'Opera';
  if (/SamsungBrowser\//.test(ua)) return 'Samsung Internet';
  if (/Firefox\/|FxiOS\//.test(ua)) return 'Firefox';
  if (/Chrome\/|CriOS\//.test(ua)) return 'Chrome';
  if (/Safari\//.test(ua)) return 'Safari';
  return 'Browser';
}

function systemName(ua: string, maxTouchPoints: number): string {
  if (/iPhone|iPod/.test(ua)) return 'iPhone';
  // iPadOS reports a desktop Mac user agent; a Mac with touch points is an iPad.
  if (/iPad/.test(ua) || (/Macintosh/.test(ua) && maxTouchPoints > 1)) return 'iPad';
  if (/Android/.test(ua)) return 'Android';
  if (/CrOS/.test(ua)) return 'ChromeOS';
  if (/Windows/.test(ua)) return 'Windows';
  if (/Macintosh|Mac OS X/.test(ua)) return 'Mac';
  if (/Linux/.test(ua)) return 'Linux';
  return 'this device';
}

/**
 * A plain name for this device from the user agent, e.g. "Chrome on Android"
 * or "Safari on iPhone". At most 80 characters, the column's limit.
 */
export function deviceLabel(
  ua: string = navigator.userAgent,
  maxTouchPoints: number = navigator.maxTouchPoints,
): string {
  return `${browserName(ua)} on ${systemName(ua, maxTouchPoints)}`.slice(0, LABEL_MAX);
}

/** This browser's row on the server, as remembered at registration. */
export interface StoredPushDevice {
  id: string;
  endpoint: string;
}

function storageKey(userId: string): string {
  return `outpost:push:${userId}`;
}

export function readStoredDevice(userId: string): StoredPushDevice | null {
  try {
    const raw = window.localStorage.getItem(storageKey(userId));
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (
      typeof parsed === 'object' &&
      parsed !== null &&
      typeof (parsed as StoredPushDevice).id === 'string' &&
      typeof (parsed as StoredPushDevice).endpoint === 'string'
    ) {
      return parsed as StoredPushDevice;
    }
    return null;
  } catch {
    return null;
  }
}

export function writeStoredDevice(userId: string, device: StoredPushDevice): void {
  try {
    window.localStorage.setItem(storageKey(userId), JSON.stringify(device));
  } catch {
    // Storage disabled. The card then shows "off" on the next visit.
  }
}

export function clearStoredDevice(userId: string): void {
  try {
    window.localStorage.removeItem(storageKey(userId));
  } catch {
    // Storage disabled; nothing was kept.
  }
}
